import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Eye,
  Globe,
  Headphones,
  Maximize2,
  Plus,
  Star,
  Trash,
  Bot,
  Zap,
} from "lucide-react";
import FeedbackDrawer from "@/components/books/FeedbackDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BookSummary, BookAuthor, SummaryContent } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MDEditor from "@uiw/react-md-editor";
import {
  fetchBookById,
  deleteBook,
  trackBookView,
  trackBookRead,
  trackBookAudioPlay,
} from "@/api/books";
import { fetchLanguages } from "@/data/mockData";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";

// Function to convert seconds to HH:MM:SS format
const secondsToTimeString = (seconds: number | undefined): string => {
  if (seconds === undefined || seconds === null) return "00:00:00";

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    secs.toString().padStart(2, "0"),
  ].join(":");
};

// Function to convert seconds to a human-readable duration (e.g., "5 min", "1h 23min")
const secondsToDuration = (seconds: number | undefined): string => {
  if (!seconds || seconds === 0) return "0 min";

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }
  return `${minutes} min`;
};

// Use system theme detection
const useThemeDetector = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkTheme(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isDarkTheme ? "dark" : "light";
};

const BookDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useThemeDetector();
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [expandedChapters, setExpandedChapters] = useState<number[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAdminTools, setShowAdminTools] = useState(false);
  const [showFeedbackDrawer, setShowFeedbackDrawer] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  // Fetch book data using React Query
  const { data: book, isLoading } = useQuery({
    queryKey: ["books", id],
    queryFn: async () => {
      if (!id) throw new Error("Book ID is required");
      return await fetchBookById(Number(id));
    },
    enabled: !!id,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale for immediate updates
  });

  // Fetch languages data
  const { data: languages } = useQuery({
    queryKey: ["languages"],
    queryFn: fetchLanguages,
  });

  // Store the current summary content based on selected language
  const [currentSummaryContent, setCurrentSummaryContent] =
    useState<SummaryContent | null>(null);

  // Set initial language when book loads
  useEffect(() => {
    if (book && book.summaryContents.length > 0 && !selectedLanguage) {
      setSelectedLanguage(book.summaryContents[0].language);
    }
  }, [book, selectedLanguage]);

  // Update current summary content when language changes or book loads
  useEffect(() => {
    if (book && selectedLanguage) {
      const content = book.summaryContents.find(
        (content) => content.language === selectedLanguage
      );
      setCurrentSummaryContent(content || null);

      // Debug logging
      console.log("Selected language:", selectedLanguage);
      console.log("Current summary content:", content);
      if (content) {
        console.log(`Chapters for ${selectedLanguage}:`, content.chapters);
      }
    }
  }, [book, selectedLanguage]);

  // Add additional debug logging for initial load
  useEffect(() => {
    if (book) {
      console.log("Book loaded:", book);
      console.log("Summary contents:", book.summaryContents);

      if (book.summaryContents.length > 0) {
        const firstContent = book.summaryContents[0];
        console.log(
          `First content (${firstContent.language}) chapters:`,
          firstContent.chapters
        );
      }
    }
  }, [book]);

  const toggleChapter = (chapterId: number) => {
    setExpandedChapters((prev) =>
      prev.includes(chapterId)
        ? prev.filter((id) => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Book ID is required for deletion");
      return await deleteBook(Number(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast({
        title: "Book deleted",
        description: book?.title
          ? `"${book.title}" has been deleted.`
          : "Book has been deleted.",
        variant: "destructive",
      });
      navigate("/admin/books");
    },
    onError: (error) => {
      console.error("Error deleting book:", error);
      toast({
        title: "Error",
        description: "Failed to delete book. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDeleteBook = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmedDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirmation(false);
  };

  // Add mutations for tracking events
  const addBookViewMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Book ID is required");

      // Get device and session info
      const platform = navigator.platform;
      const appVersion = "1.0.0";
      const deviceModel = navigator.userAgent.includes("Mobile")
        ? "Mobile"
        : "Desktop";
      const osVersion = navigator.userAgent;

      // Create session data for view tracking
      const sessionData = {
        source: "web",
        app_version: appVersion,
        platform,
        device_model: deviceModel,
        os_version: osVersion,
        ip_address: "127.0.0.1",
      };

      const result = await trackBookView(
        Number(id),
        currentUser?.id, // Add authenticated user ID
        sessionData
      );

      return {
        ...result,
        trackingInfo: {
          bookId: Number(id),
          userId: currentUser?.id,
          userEmail: currentUser?.email,
          userName: currentUser?.name,
          sessionData,
        },
      };
    },
    onSuccess: (data) => {
      // Refetch the book data to get the actual count from database
      queryClient.invalidateQueries({ queryKey: ["books", id] });
      console.log("View tracking result:", data.message);

      toast({
        title: "View recorded",
        description: `View has been recorded for this book using 5-minute window deduplication`,
        variant: "default",
      });
    },
    onError: (error) => {
      console.error("Error recording view:", error);
      toast({
        title: "Error",
        description: "Failed to record view",
        variant: "destructive",
      });
    },
  });

  const addBookReadMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Book ID is required");

      // Get first chapter if available
      const chapterId = currentSummaryContent?.chapters?.[0]?.id;

      // Get device and session info
      const platform = navigator.platform;
      const appVersion = "1.0.0";
      const deviceModel = navigator.userAgent.includes("Mobile")
        ? "Mobile"
        : "Desktop";
      const osVersion = navigator.userAgent;

      // Create test read session data
      const sessionData = {
        duration_seconds: 600, // 10 minutes
        pages_read: 5,
        read_progress: 0.5,
        app_version: appVersion,
        platform,
        device_model: deviceModel,
        os_version: osVersion,
        ip_address: "127.0.0.1",
      };

      await trackBookRead(
        Number(id),
        chapterId,
        currentUser?.id, // Add authenticated user ID
        sessionData
      );

      return {
        success: true,
        trackingInfo: {
          bookId: Number(id),
          chapterId,
          userId: currentUser?.id,
          userEmail: currentUser?.email,
          userName: currentUser?.name,
          sessionData,
        },
      };
    },
    onSuccess: (data) => {
      // Refetch the book data to get the actual count from database
      queryClient.invalidateQueries({ queryKey: ["books", id] });
      console.log("Read tracking details:", data.trackingInfo);
      toast({
        title: "Read recorded",
        description: `A new read has been recorded for this book by ${
          currentUser?.name || "you"
        }`,
        variant: "default",
      });
    },
    onError: (error) => {
      console.error("Error recording read:", error);
      toast({
        title: "Error",
        description: "Failed to record read",
        variant: "destructive",
      });
    },
  });

  const addBookAudioPlayMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Book ID is required");

      // Get first chapter if available
      const chapterId = currentSummaryContent?.chapters?.[0]?.id;

      // Get device and session info
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const appVersion = "1.0.0";
      const deviceModel = navigator.userAgent.includes("Mobile")
        ? "Mobile"
        : "Desktop";
      const osVersion = navigator.userAgent;

      // Create test audio play session data
      const sessionData = {
        duration_seconds: 300, // 5 minutes
        playback_position: 180, // 3 minutes position
        playback_speed: 1.5,
        was_completed: false,
        app_version: appVersion,
        platform,
        device_model: deviceModel,
        os_version: osVersion,
        ip_address: "127.0.0.1",
      };

      // Using simplified test function (internally uses new event-based system)
      await trackBookAudioPlay(
        Number(id),
        chapterId,
        currentUser?.id, // Add authenticated user ID
        sessionData
      );

      return {
        success: true,
        trackingInfo: {
          bookId: Number(id),
          chapterId,
          userId: currentUser?.id,
          userEmail: currentUser?.email,
          userName: currentUser?.name,
          sessionData,
        },
      };
    },
    onSuccess: (data) => {
      // Refetch the book data to get the actual count from database
      queryClient.invalidateQueries({ queryKey: ["books", id] });
      console.log("Audio play tracking details:", data.trackingInfo);
      toast({
        title: "Audio play recorded",
        description: `A new audio play has been recorded for this book by ${
          currentUser?.name || "you"
        }`,
        variant: "default",
      });
    },
    onError: (error) => {
      console.error("Error recording audio play:", error);
      toast({
        title: "Error",
        description: "Failed to record audio play",
        variant: "destructive",
      });
    },
  });

  // Modified Engagement Card with Test Buttons
  const renderEngagementCard = () => {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Engagement</CardTitle>
          <CardDescription>Book performance metrics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Rating</span>
            <button
              onClick={() => setShowFeedbackDrawer(true)}
              className="flex items-center gap-1 hover:opacity-80 transition-opacity"
            >
              {book?.rating && book.rating > 0 ? (
                <>
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium">
                    {book.rating.toFixed(1)}
                  </span>
                  {book.ratingCount && book.ratingCount > 0 ? (
                    <span className="text-xs text-muted-foreground">
                      ({book.ratingCount} reviews)
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="text-xs text-muted-foreground">
                  No ratings yet
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Views</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <span
                  className={`font-medium ${
                    addBookViewMutation.isPending ? "text-primary" : ""
                  }`}
                >
                  {(book?.viewCount || 0).toLocaleString()}
                </span>
              </div>
              {showAdminTools && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => addBookViewMutation.mutate()}
                        disabled={addBookViewMutation.isPending}
                      >
                        {addBookViewMutation.isPending ? (
                          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        ) : (
                          <Plus className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add test view (5-min window deduplication)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Reads</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span
                  className={`font-medium ${
                    addBookReadMutation.isPending ? "text-primary" : ""
                  }`}
                >
                  {book?.read_count || 0}
                </span>
              </div>
              {showAdminTools && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => addBookReadMutation.mutate()}
                        disabled={addBookReadMutation.isPending}
                      >
                        {addBookReadMutation.isPending ? (
                          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        ) : (
                          <Plus className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add test read</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Audio Plays</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Headphones className="h-4 w-4 text-muted-foreground" />
                <span
                  className={`font-medium ${
                    addBookAudioPlayMutation.isPending ? "text-primary" : ""
                  }`}
                >
                  {book?.audio_play_count || 0}
                </span>
              </div>
              {showAdminTools && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => addBookAudioPlayMutation.mutate()}
                        disabled={
                          addBookAudioPlayMutation.isPending ||
                          !currentSummaryContent?.audioUrl
                        }
                      >
                        {addBookAudioPlayMutation.isPending ? (
                          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        ) : (
                          <Plus className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {currentSummaryContent?.audioUrl
                          ? "Add test audio play (event-based tracking)"
                          : "No audio available"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          <div className="mt-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Testing tools (view: 5-min window, audio: event-based)
              </span>
              <Switch
                checked={showAdminTools}
                onCheckedChange={setShowAdminTools}
                aria-label="Toggle admin tools"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading || !book) {
    return (
      <AdminLayout title="Book Details">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Book Details">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{book.title}</h1>
              {book.isActive ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  Published
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300">
                  Unpublished
                </span>
              )}
              {book.isFree ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  Free
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Premium
                </span>
              )}
            </div>
            {currentSummaryContent?.subtitle && (
              <p className="text-sm text-muted-foreground">
                {currentSummaryContent.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="default"
            onClick={() => navigate(`/admin/books/${id}/edit`)}
          >
            <Edit className="mr-2 h-4 w-4" /> Edit Book
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteBook}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <div className="animate-spin h-4 w-4 mr-2 border-2 border-foreground rounded-full border-t-transparent"></div>
            ) : (
              <Trash className="mr-2 h-4 w-4" />
            )}
            Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Book Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Authors
                </p>
                <p className="text-lg font-medium">
                  {book.authors.length > 0
                    ? book.authors.map((author) => author.name).join(", ")
                    : "Unknown"}
                </p>
              </div>

              {currentSummaryContent?.translator && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Translator
                  </p>
                  <p className="text-lg font-medium">
                    {currentSummaryContent.translator.name}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Reading Time
                </p>
                <p className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-lg font-medium">
                    {book.readingTime} minutes
                  </span>
                </p>
              </div>

              {(book.isbn || book.publisher || book.publicationYear || book.pageCount) && (
                <>
                  {book.publisher && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Publisher
                      </p>
                      <p className="text-sm">{book.publisher}</p>
                    </div>
                  )}
                  {book.publicationYear && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Publication Year
                      </p>
                      <p className="text-sm">{book.publicationYear}</p>
                    </div>
                  )}
                  {book.pageCount && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Pages
                      </p>
                      <p className="text-sm">{book.pageCount}</p>
                    </div>
                  )}
                  {book.isbn && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        ISBN
                      </p>
                      <p className="text-sm font-mono">{book.isbn}</p>
                    </div>
                  )}
                </>
              )}

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Categories
                </p>
                <div className="flex flex-wrap gap-1">
                  {book.categories
                    ?.filter((category) => category)
                    ?.map((category) => (
                      <span
                        key={category?.id || "unknown"}
                        className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full"
                      >
                        {category?.name || "Unknown"}
                      </span>
                    ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Languages
                </p>
                <div className="flex flex-wrap gap-2">
                  {book.summaryContents
                    .map((content) => content.language)
                    .filter(
                      (language, index, self) =>
                        self.indexOf(language) === index
                    ) // Get unique languages
                    .map((languageCode) => {
                      const summaryContent = book.summaryContents.find(
                        (content) => content.language === languageCode
                      );

                      // Find language details
                      const languageName =
                        languages?.find((lang) => lang.code === languageCode)
                          ?.name || languageCode;

                      return (
                        <span
                          key={languageCode}
                          className={`px-2.5 py-1 text-xs rounded-full flex items-center gap-1.5 ${
                            languageCode === selectedLanguage
                              ? "bg-primary text-primary-foreground"
                              : "bg-secondary text-secondary-foreground"
                          }`}
                        >
                          {summaryContent?.contentType === "full_book" ? (
                            <BookOpen className={`h-3 w-3 ${
                              languageCode === selectedLanguage
                                ? "text-white"
                                : "text-violet-600 dark:text-violet-400"
                            }`} />
                          ) : (
                            <Zap className={`h-3 w-3 ${
                              languageCode === selectedLanguage
                                ? "text-white"
                                : "text-amber-500"
                            }`} />
                          )}
                          {languageName}
                          <div className="flex items-center gap-1">
                            {summaryContent?.chapters?.length > 0 && (
                              <div
                                className={`flex items-center gap-0.5 px-1 rounded-sm ${
                                  languageCode === selectedLanguage
                                    ? "bg-white/20 text-white"
                                    : "bg-primary/10 text-primary"
                                }`}
                              >
                                <span className="text-[10px] font-medium">
                                  {summaryContent.chapters.length}
                                </span>
                              </div>
                            )}
                            {summaryContent?.audioUrl && (
                              <Headphones className="h-3 w-3" />
                            )}
                            {summaryContent?.embeddingCount != null && summaryContent.embeddingCount > 0 && (
                              <div
                                className={`flex items-center gap-0.5 px-1 rounded-sm ${
                                  languageCode === selectedLanguage
                                    ? "bg-white/20 text-white"
                                    : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                }`}
                              >
                                <Bot className="h-2.5 w-2.5" />
                                <span className="text-[10px] font-medium">
                                  {summaryContent.embeddingCount}
                                </span>
                              </div>
                            )}
                          </div>
                        </span>
                      );
                    })}
                </div>
              </div>
            </CardContent>
          </Card>

          {book.summaryContents.length > 0 && (
            <div className="flex items-center gap-3 p-4 mb-6 bg-muted/30 rounded-lg border border-border/50">
              <Globe className="h-5 w-5 text-primary" />
              <Select
                value={selectedLanguage}
                onValueChange={setSelectedLanguage}
              >
                <SelectTrigger className="w-[200px] border-border/50">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {book.summaryContents
                    .map((content) => content.language)
                    .filter(
                      (language, index, self) =>
                        self.indexOf(language) === index
                    ) // Get unique languages
                    .map((languageCode) => {
                      const summaryContent = book.summaryContents.find(
                        (content) => content.language === languageCode
                      );

                      // Find language details
                      const languageName =
                        languages?.find((lang) => lang.code === languageCode)
                          ?.name || languageCode;

                      return (
                        <SelectItem key={languageCode} value={languageCode}>
                          <div className="flex items-center gap-2">
                            {summaryContent?.contentType === "full_book" ? (
                              <BookOpen className="h-3 w-3 text-violet-600 dark:text-violet-400" />
                            ) : (
                              <Zap className="h-3 w-3 text-amber-500" />
                            )}
                            {languageName}
                            <span className="text-xs text-muted-foreground">
                              {summaryContent?.chapters?.length || 0} chapters
                            </span>
                            {summaryContent?.audioUrl && (
                              <Headphones className="h-3 w-3 text-primary" />
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              <span className="text-sm">
                Viewing content in{" "}
                <span className="font-medium text-foreground">
                  {languages?.find((l) => l.code === selectedLanguage)?.name ||
                    selectedLanguage}
                </span>
              </span>
              {currentSummaryContent?.embeddingCount != null && currentSummaryContent.embeddingCount > 0 ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <Bot className="h-3 w-3" />
                  AI Ready ({currentSummaryContent.embeddingCount} chunks)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
                  <Bot className="h-3 w-3" />
                  No Embeddings
                </span>
              )}
            </div>
          )}

          {currentSummaryContent?.shortDescription && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
                <CardDescription>
                  Overview in{" "}
                  {languages?.find((l) => l.code === selectedLanguage)?.name ||
                    selectedLanguage}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  data-color-mode={theme}
                  className={theme === "dark" ? "dark-markdown-container" : ""}
                >
                  <MDEditor.Markdown
                    source={currentSummaryContent.shortDescription}
                    className={`prose max-w-none ${
                      theme === "dark" ? "prose-invert" : ""
                    }`}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {book?.authors?.some((a: BookAuthor) => a.bookBio) && (
            <Card>
              <CardHeader>
                <CardTitle>About the Authors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {book.authors
                  .filter((a: BookAuthor) => a.bookBio)
                  .map((author: BookAuthor) => (
                    <div key={author.id}>
                      <h4 className="font-medium mb-1">{author.name}</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {author.bookBio}
                      </p>
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>
                {currentSummaryContent?.contentType === "full_book" ? "Chapters" : "Chapter Summaries"}
              </CardTitle>
              <CardDescription>
                {currentSummaryContent?.chapters?.length || 0}{" "}
                {currentSummaryContent?.contentType === "full_book" ? "chapters" : "chapter summaries"}{" "}
                in{" "}
                {languages?.find((l) => l.code === selectedLanguage)?.name ||
                  selectedLanguage}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!currentSummaryContent ||
              !currentSummaryContent.chapters ||
              currentSummaryContent.chapters.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-muted/20">
                  <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">
                    No {currentSummaryContent?.contentType === "full_book" ? "chapters" : "chapter summaries"} available in this language.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Sort chapters by position before displaying */}
                  {[...currentSummaryContent.chapters]
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((chapter) => (
                      <div
                        key={chapter.id}
                        className="border rounded-md overflow-hidden shadow-sm"
                      >
                        <div
                          className="flex items-center gap-3 px-4 py-3 bg-muted/20 cursor-pointer"
                          onClick={() => toggleChapter(chapter.id)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {chapter.label ||
                                  `Chapter ${chapter.position + 1}`}
                              </span>
                              {chapter.startTime !== undefined &&
                                chapter.startTime !== null && (
                                  <span className="inline-flex items-center gap-0.5 text-xs bg-muted rounded-sm px-1.5 py-0.5">
                                    <Clock className="h-3 w-3 text-muted-foreground" />
                                    {secondsToTimeString(chapter.startTime)}
                                  </span>
                                )}
                            </div>
                            <h3 className="font-medium">
                              {chapter.title || "Untitled Chapter"}
                            </h3>
                          </div>
                          <button
                            className="p-1 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleChapter(chapter.id);
                            }}
                          >
                            {expandedChapters.includes(chapter.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </div>

                        {expandedChapters.includes(chapter.id) && (
                          <div className="p-4 border-t">
                            <div
                              data-color-mode={theme}
                              className={
                                theme === "dark"
                                  ? "dark-markdown-container"
                                  : ""
                              }
                            >
                              <MDEditor.Markdown
                                source={
                                  chapter.content || "No content available"
                                }
                                className={`prose max-w-none ${
                                  theme === "dark" ? "prose-invert" : ""
                                }`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Book Cover</CardTitle>
            </CardHeader>
            <CardContent>
              {book.coverImage ? (
                <div
                  className="rounded-md overflow-hidden border flex items-center justify-center p-4 relative"
                  style={{ backgroundColor: "#1F2532" }}
                >
                  {/* Dynamic background color overlay with 80% transparency */}
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundColor: book.coverBgColor || "#E9ECEF",
                      opacity: 0.8,
                    }}
                  />
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="max-w-full max-h-60 object-contain relative z-10"
                  />
                </div>
              ) : (
                <div
                  className="rounded-md border h-40 flex items-center justify-center text-muted-foreground relative"
                  style={{ backgroundColor: "#1F2532" }}
                >
                  {/* Dynamic background color overlay with 80% transparency */}
                  <div
                    className="absolute inset-0 rounded-md"
                    style={{
                      backgroundColor: book.coverBgColor || "#E9ECEF",
                      opacity: 0.8,
                    }}
                  />
                  <span className="relative z-10">No cover image</span>
                </div>
              )}
            </CardContent>
          </Card>

          {renderEngagementCard()}

          {currentSummaryContent?.audioUrl && (
            <Card className="overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <Headphones className="h-6 w-6 text-primary mt-0.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold truncate">
                        {currentSummaryContent.audioTitle || "Audio summary"}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="truncate">
                        {currentSummaryContent.audioNarrator?.name ||
                          "No narrator assigned"}
                      </span>
                      {currentSummaryContent.audioDuration && (
                        <>
                          <span className="text-xs">•</span>
                          <span className="whitespace-nowrap">
                            {secondsToDuration(currentSummaryContent.audioDuration)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <audio
                    controls
                    className="w-full theme-audio-player"
                    src={currentSummaryContent.audioUrl}
                    id="audio-player"
                  ></audio>
                </div>
              </div>

              {currentSummaryContent.chapters.length > 0 &&
                currentSummaryContent.chapters.some(
                  (ch) => ch.startTime !== undefined
                ) && (
                  <div className="border-t">
                    <div className="p-4">
                      <div className="space-y-2">
                        {currentSummaryContent.chapters
                          .filter((chapter) => chapter.startTime !== undefined)
                          .sort((a, b) => (a.position || 0) - (b.position || 0))
                          .map((chapter) => (
                            <button
                              key={chapter.id}
                              onClick={() => {
                                const audio = document.getElementById(
                                  "audio-player"
                                ) as HTMLAudioElement;
                                if (audio && chapter.startTime !== undefined) {
                                  audio.currentTime = chapter.startTime;
                                  audio
                                    .play()
                                    .catch((e) =>
                                      console.error("Could not play audio:", e)
                                    );
                                }
                              }}
                              className="flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md hover:bg-accent group transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-muted-foreground truncate">
                                    {chapter.label ||
                                      `Chapter ${chapter.position + 1}`}
                                  </span>
                                  {chapter.startTime !== undefined &&
                                    chapter.startTime !== null && (
                                      <span className="inline-flex items-center gap-0.5 text-xs bg-muted/70 rounded-sm px-1.5 py-0.5 flex-shrink-0">
                                        <Clock className="h-3 w-3 text-muted-foreground" />
                                        {secondsToTimeString(chapter.startTime)}
                                      </span>
                                    )}
                                </div>
                                <p className="font-medium group-hover:text-primary truncate">
                                  {chapter.title || "Untitled Chapter"}
                                </p>
                              </div>
                              <div className="flex-shrink-0 ml-2">
                                <span className="h-5 w-5 flex items-center justify-center text-primary bg-primary/10 rounded-full">
                                  ▶
                                </span>
                              </div>
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book Summary</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{book.title}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feedback Drawer */}
      <FeedbackDrawer
        open={showFeedbackDrawer}
        onClose={() => setShowFeedbackDrawer(false)}
        bookId={Number(id)}
        bookTitle={book.title}
      />
    </AdminLayout>
  );
};

export default BookDetails;
